import { filterPredicateIsTruthy } from 'utility/Check/filterPredicateIsTruthy';

import {
    AccommodationFilter,
    CancellationType,
    Filter,
    FlightFilter,
    PaymentTypeFilter,
    RangeValue,
    SpecialFilter,
    TourOperatorCategory,
    TransferFilter,
} from 'types/Search/filter';
import {
    SearchParam,
    paramTruthyValue,
    paramTransferValue,
} from 'types/Search/params';

import { ViewType } from 'proto/Offer/Accommodation/ViewType';

import { convertAccommodationCategoryToUrlValue } from './Filter/accommodationCategory';
import { convertAttributesListToUrlParams } from './Filter/attributeList';
import { convertExteriorToUrlValue } from './Filter/exterior';
import { convertMealTypeToUrlValue } from './Filter/mealType';
import { convertPoolTypeToUrlValue } from './Filter/poolType';
import { convertRoomClassToUrlValue } from './Filter/roomClass';
import { convertRoomTypeToUrlValue } from './Filter/roomType';
import { convertViewTypeToUrlValue } from './Filter/viewType';

const convertBudgetToUrlParams = (budget: RangeValue<number> | undefined) => {
    const urlParams = new URLSearchParams();
    if (budget) {
        budget.min &&
            urlParams.append(SearchParam.BudgetMin, budget.min.toString());
        budget.max &&
            urlParams.append(SearchParam.BudgetMax, budget.max.toString());
    }

    return urlParams;
};

const convertTourOperatorListToUrlParams = (tourOperatorList: Set<string>) => {
    const urlParams = new URLSearchParams();
    if (tourOperatorList.size) {
        urlParams.append(
            SearchParam.TouroperatorList,
            Array.from(tourOperatorList).join(',')
        );
    }

    return urlParams;
};

const convertFlightToUrlParams = (flight: FlightFilter) => {
    const urlParams = new URLSearchParams();
    const {
        airlineList,
        destinationAirportList,
        directFlight,
        hideMixFlights,
        withNoBag,
        departureFlightTime,
        returnFlightTime,
    } = flight;

    if (airlineList.size) {
        urlParams.append(
            SearchParam.AirlineList,
            Array.from(airlineList).join(',')
        );
    }
    if (destinationAirportList.size) {
        urlParams.append(
            SearchParam.DestinationAirportList,
            Array.from(destinationAirportList).join(',')
        );
    }
    if (directFlight) {
        urlParams.append(SearchParam.DirectFlight, paramTruthyValue);
    }
    if (hideMixFlights) {
        urlParams.append(SearchParam.HideMixFlights, paramTruthyValue);
    }
    if (withNoBag) {
        urlParams.append(SearchParam.WithNoBag, paramTruthyValue);
    }
    if (departureFlightTime) {
        departureFlightTime.min &&
            urlParams.append(
                SearchParam.DepartureFlightTimeFrom,
                departureFlightTime.min.toString()
            );
        departureFlightTime.max &&
            urlParams.append(
                SearchParam.DepartureFlightTimeUntil,
                departureFlightTime.max.toString()
            );
    }
    if (returnFlightTime) {
        returnFlightTime.min &&
            urlParams.append(
                SearchParam.ReturnFlightTimeFrom,
                returnFlightTime.min.toString()
            );
        returnFlightTime.max &&
            urlParams.append(
                SearchParam.ReturnFlightTimeUntil,
                returnFlightTime.max.toString()
            );
    }

    return urlParams;
};

const convertTransferToUrlParams = (transfer: TransferFilter) => {
    const { hotelTransfer, privateTransfer, railAndFly } = transfer;
    const urlParams = new URLSearchParams();

    if (hotelTransfer) {
        urlParams.append(SearchParam.HotelTransfer, paramTransferValue);
    }
    if (privateTransfer) {
        urlParams.append(SearchParam.PrivateTransfer, paramTruthyValue);
    }
    if (railAndFly) {
        urlParams.append(SearchParam.RailAndFly, paramTruthyValue);
    }

    return urlParams;
};

const convertPaymentTypeListToUrlParams = (
    paymentTypeList: Set<PaymentTypeFilter>
) => {
    const urlParams = new URLSearchParams();
    if (paymentTypeList.size) {
        urlParams.append(
            SearchParam.PaymentTypeList,
            Array.from(paymentTypeList).join(',')
        );
    }

    return urlParams;
};

const convertTourOperatorCategoryListToUrlParams = (
    touroperatorCatgeroryList: Set<TourOperatorCategory>
) => {
    const urlParams = new URLSearchParams();
    if (touroperatorCatgeroryList.size) {
        urlParams.append(
            SearchParam.TourOperatorCategoryList,
            Array.from(touroperatorCatgeroryList).join(',')
        );
    }

    return urlParams;
};

const convertSpecialToUrlParams = (special: SpecialFilter) => {
    const urlParams = new URLSearchParams();
    if (special.cancellation) {
        const isCancellationCost =
            special.cancellation === CancellationType.Cost;
        const isCancellationFree =
            special.cancellation === CancellationType.Free;
        const isCancellationFree24Hour =
            special.cancellation === CancellationType.Free24Hour;

        if (isCancellationCost) {
            urlParams.append(SearchParam.CancellationCost, paramTruthyValue);
        }
        if (isCancellationFree) {
            urlParams.append(SearchParam.CancellationFree, paramTruthyValue);
        }
        if (isCancellationFree24Hour) {
            urlParams.append(SearchParam.Cancellation24Hours, paramTruthyValue);
        }
    }

    if (special.lowDeposit) {
        urlParams.append(SearchParam.LowDeposit, paramTruthyValue);
    }

    if (special.rebookable) {
        urlParams.append(SearchParam.Rebookable, paramTruthyValue);
    }

    if (special.reservable) {
        urlParams.append(SearchParam.ReservablePredicted, paramTruthyValue);
    }

    if (special.promoDiscount) {
        urlParams.append(SearchParam.PromoDiscount, paramTruthyValue);
    }

    return urlParams;
};

const convertAccommodationToUrlParams = (
    accommodation: AccommodationFilter
) => {
    const {
        mealTypeList,
        roomTypeList,
        roomClassList,
        viewTypeList,
        poolTypeList,
        exteriorList,
        hotelCategoryList,
        rating,
        accommodationCategoryList,
        attributesList,
        withHygieneFacts,
    } = accommodation;
    const urlParams = new URLSearchParams();

    if (mealTypeList.size) {
        urlParams.append(
            SearchParam.MealTypeList,
            Array.from(mealTypeList)
                .map(convertMealTypeToUrlValue)
                .filter(filterPredicateIsTruthy)
                .join(',')
        );
    }
    if (roomTypeList.size) {
        urlParams.append(
            SearchParam.RoomTypeList,
            Array.from(roomTypeList)
                .sort()
                .map(convertRoomTypeToUrlValue)
                .filter(filterPredicateIsTruthy)
                .join(',')
        );
    }
    if (roomClassList.size) {
        urlParams.append(
            SearchParam.RoomClassList,
            Array.from(roomClassList)
                .sort()
                .map(convertRoomClassToUrlValue)
                .join(',')
        );
    }
    if (viewTypeList.size) {
        urlParams.append(
            SearchParam.ViewTypeList,
            Array.from(viewTypeList)
                .sort()
                .map(convertViewTypeToUrlValue)
                .filter(filterPredicateIsTruthy)
                .join(',')
        );
        if (viewTypeList.has(ViewType.SeaView)) {
            urlParams.append(SearchParam.OceanView, paramTruthyValue);
        }
        if (viewTypeList.has(ViewType.LateralSeaView)) {
            urlParams.append(SearchParam.PartialOceanView, paramTruthyValue);
        }
    }
    if (poolTypeList.size) {
        urlParams.append(
            SearchParam.PoolTypeList,
            Array.from(poolTypeList)
                .sort()
                .map(convertPoolTypeToUrlValue)
                .filter(filterPredicateIsTruthy)
                .join(',')
        );
    }
    if (exteriorList.size) {
        urlParams.append(
            SearchParam.ExteriorList,
            Array.from(exteriorList)
                .sort()
                .map(convertExteriorToUrlValue)
                .filter(filterPredicateIsTruthy)
                .join(',')
        );
    }
    if (hotelCategoryList.size) {
        urlParams.append(
            SearchParam.HotelCategoryList,
            Array.from(hotelCategoryList).join(',')
        );
    }
    if (rating) {
        urlParams.append(SearchParam.Rating, rating.toString());
    }
    if (accommodationCategoryList.size) {
        urlParams.append(
            SearchParam.AccommodationCategoryList,
            Array.from(accommodationCategoryList)
                .sort()
                .map(convertAccommodationCategoryToUrlValue)
                .join(',')
        );
    }

    if (withHygieneFacts) {
        urlParams.append(SearchParam.WithHygieneFacts, paramTruthyValue);
    }

    return new URLSearchParams({
        ...Object.fromEntries(urlParams),
        // convert attributesList
        ...Object.fromEntries(convertAttributesListToUrlParams(attributesList)),
    });
};

export const convertFilterToUrlParams = (filter: Filter): URLSearchParams => {
    const filterSearchParams = new URLSearchParams({
        // convert Filter into urlParams
        // budget
        ...Object.fromEntries(convertBudgetToUrlParams(filter.budget)),
        // tourOperatorList
        ...Object.fromEntries(
            convertTourOperatorListToUrlParams(filter.tourOperatorList)
        ),
        // flight
        ...Object.fromEntries(convertFlightToUrlParams(filter.flight)),
        // transfer
        ...Object.fromEntries(convertTransferToUrlParams(filter.transfer)),
        // paymentTypeList
        ...Object.fromEntries(
            convertPaymentTypeListToUrlParams(filter.paymentTypeList)
        ),
        // tourOperatorCategoryList
        ...Object.fromEntries(
            convertTourOperatorCategoryListToUrlParams(
                filter.tourOperatorCategoryList
            )
        ),
        // special
        ...Object.fromEntries(convertSpecialToUrlParams(filter.special)),
        // accommodation
        ...Object.fromEntries(
            convertAccommodationToUrlParams(filter.accommodation)
        ),
    });
    filterSearchParams.sort();

    return filterSearchParams;
};
